import { useState } from 'react';
import {
  Box, Typography, Card, CardContent, CardActionArea, Chip,
  Stack, Dialog, DialogTitle, DialogContent, DialogContentText,
  Divider, IconButton,
} from '@mui/material';
import Iconify from '../../../../components/Iconify';

const PRIORITY_COLOR = { LOW: 'default', MEDIUM: 'info', HIGH: 'warning', CRITICAL: 'error' };
const STATUS_COLOR = {
  OPEN: 'error', IN_PROGRESS: 'warning', PENDING: 'info', RESOLVED: 'success', CLOSED: 'default',
};

function SRDetailDialog({ sr, open, onClose }) {
  if (!sr) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{sr.sr_id} — {sr.title}</Typography>
          <IconButton onClick={onClose} size="small"><Iconify icon="mdi:close" /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1}>
            <Chip label={sr.status} color={STATUS_COLOR[sr.status] || 'default'} size="small" />
            <Chip label={sr.priority} color={PRIORITY_COLOR[sr.priority] || 'default'} size="small" variant="outlined" />
          </Stack>
          <Box>
            <Typography variant="caption" color="text.secondary">Description</Typography>
            <Typography variant="body2">{sr.description || '—'}</Typography>
          </Box>
          <Divider />
          <Stack direction="row" spacing={4}>
            <Box>
              <Typography variant="caption" color="text.secondary">Requester</Typography>
              <Typography variant="body2">{sr.requester}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Assignee</Typography>
              <Typography variant="body2">{sr.assignee}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Category</Typography>
              <Typography variant="body2">{sr.category}</Typography>
            </Box>
          </Stack>
          {sr.due_date && (
            <Box>
              <Typography variant="caption" color="text.secondary">Due Date</Typography>
              <Typography variant="body2">{sr.due_date}</Typography>
            </Box>
          )}
          {sr.resolution_note && (
            <Box>
              <Typography variant="caption" color="text.secondary">Resolution</Typography>
              <Typography variant="body2">{sr.resolution_note}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary">Opened</Typography>
            <Typography variant="body2">{sr.created_at} ({sr.days_open} days ago)</Typography>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function SRCard({ sr }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Card
        variant="outlined"
        sx={{
          minWidth: 220,
          maxWidth: 260,
          flexShrink: 0,
          borderLeft: 4,
          borderLeftColor: `${PRIORITY_COLOR[sr.priority] || 'grey'}.main`,
          '&:hover': { boxShadow: 3 },
        }}
      >
        <CardActionArea onClick={() => setOpen(true)} sx={{ p: 0 }}>
          <CardContent sx={{ pb: '12px !important' }}>
            <Stack spacing={0.75}>
              <Typography variant="caption" color="text.secondary">{sr.sr_id}</Typography>
              <Typography variant="body2" fontWeight={600} noWrap>{sr.title}</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                <Chip label={sr.status} color={STATUS_COLOR[sr.status] || 'default'} size="small" sx={{ height: 18, fontSize: 10 }} />
                <Chip label={sr.priority} color={PRIORITY_COLOR[sr.priority] || 'default'} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {sr.requester} · {sr.days_open}d open
              </Typography>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
      <SRDetailDialog sr={sr} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export default function ServiceRequestCards({ block }) {
  const { title, cards = [] } = block;
  return (
    <Box sx={{ my: 1.5 }}>
      {title && <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{title}</Typography>}
      {cards.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No service requests found.</Typography>
      ) : (
        <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
          {cards.map((sr) => <SRCard key={sr.id || sr.pk} sr={sr} />)}
        </Box>
      )}
    </Box>
  );
}
